import React, { useState, useEffect, Suspense } from 'react';
import toast from 'react-hot-toast';
import fuenteImg from '../assets/pileta_soleada.png';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import api from '../services/api.js';
import {Clock, Mail, MapPin, Phone} from 'lucide-react';
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Map from '../components/Map.jsx';
import quienesSomosService from "../services/quienesSomosService.js";

// 1. Definimos el esquema fuera para que no se recree en cada render
const contactSchema = z.object({
  nombre: z.string().min(3, "El nombre debe tener al menos 3 caracteres"),
  email: z.string().email("Formato de correo electrónico inválido"),
  mensaje: z.string().min(10, "El mensaje es muy corto (mínimo 10 caracteres)"),
});

export default function Contacto() {

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const { executeRecaptcha } = useGoogleReCaptcha();
  const position = [-36.42348622874976, -71.95865205208577];
  const [showMap, setShowMap] = useState(false);
  const [enviadoConExito, setEnviadoConExito] = useState(false);

  // Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: { nombre: '', email: '', mensaje: '' }
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShowMap(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const fetchData = async () => {
    try {
      const res = await quienesSomosService.get();
      setData(res);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="spinner spinner-lg"></div>
        </div>
    );
  }

  if (!data) return null;

  // 2. Función de envío que recibe los datos YA validados por Zod
  const onActualSubmit = async (data) => {
    if (!executeRecaptcha) {
      toast.error('ReCAPTCHA no está listo aún.');
      return;
    }

    try {
      const recaptchaToken = await executeRecaptcha('contact_form');

      // Aquí enviamos a tu API de PHP (Ajusta el endpoint según tu backend)

      const res = await api.post(
          '/contacto',
          { ...data }, // Segundo argumento: Body
          {
            headers: {
              'X-Recaptcha-Token': recaptchaToken // Tercer argumento: Configuración
            }
          }
      );
      setEnviadoConExito(true);
      toast.success('¡Mensaje enviado con éxito!');
    } catch (err) {
      // ESTO es lo que necesitamos ver ahora:
      console.log("Error status:", err.response?.status);
      console.log("Error data:", err.response?.data); // <--- Aquí Laravel suele mandar el mensaje real

      if (err.response?.data?.exception) {
        alert("Error en Laravel: " + err.response.data.message);
      }
      toast.error('Error 500: Revisa la consola del navegador');
    }
  };

  return (
      <div>
        {/* ── Banner ── */}
        <section className="animate-fade-in" style={{
          width: '100%', height: 'min(500px, 70vh)', display: 'flex', position: 'relative',
          background: '#f7f4f0', overflow: 'hidden', borderBottom: '1px solid #d8d0c8'
        }}>
          <img src={fuenteImg} alt="Contacto" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <h1 style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            color: 'white', fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 800, textShadow: '0 4px 10px rgba(0,0,0,0.4)', zIndex: 2
          }}>
            Contacto
          </h1>
        </section>

        {/* ── Contenido ── */}
        <section style={{ background: 'var(--bg-body)', padding: '4rem 0', position: 'relative' }}>
          <div className="container" style={{ position: 'relative' }}>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2rem', maxWidth: 1080, margin: '0 auto' }}>

              {/* Formulario */}
              <div className="glass-card animate-scale-in" style={{ textAlign: 'left', background: 'var(--bg-surface)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
                  <Mail size={24} />
                  <h2 style={{ fontSize: '1.3rem', margin: 0 }}>Formulario de contacto</h2>
                </div>

                {/* handleSubmit de react-hook-form envuelve nuestra función */}
                <form onSubmit={handleSubmit(onActualSubmit)}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', marginBottom: '2rem' }}>

                    <div className="form-group">
                      <label>Nombre completo</label>
                      <input
                          {...register("nombre")}
                          className={errors.nombre ? 'input-error' : ''}
                          placeholder="Nombre y apellido"
                      />
                      {errors.nombre && <span style={{color: 'red', fontSize: '0.8rem'}}>{errors.nombre.message}</span>}
                    </div>

                    <div className="form-group">
                      <label>Correo Electrónico</label>
                      <input
                          {...register("email")}
                          className={errors.email ? 'input-error' : ''}
                          placeholder="hola@hola.cl"
                      />
                      {errors.email && <span style={{color: 'red', fontSize: '0.8rem'}}>{errors.email.message}</span>}
                    </div>

                    <div className="form-group">
                      <label>Mensaje</label>
                      <textarea
                          {...register("mensaje")}
                          className={errors.mensaje ? 'input-error' : ''}
                          placeholder="¿En qué podemos ayudarle?"
                          style={{ minHeight: '100px', padding: '0.8rem', borderRadius: '8px', border: '1px solid #ccc' }}
                      />
                      {errors.mensaje && <span style={{color: 'red', fontSize: '0.8rem'}}>{errors.mensaje.message}</span>}
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%',cursor: (isSubmitting || enviadoConExito) ? 'not-allowed' : 'pointer' }}
                          disabled={isSubmitting || enviadoConExito}>
                    {isSubmitting ? (
                        'Enviando...'
                    ) : enviadoConExito ? (
                        '¡Mensaje enviado!'
                    ) : (
                        'Enviar Mensaje'
                    )}
                  </button>
                </form>
              </div>

              {/* Columna Derecha: Mapa e Información */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                
                {/* Mapa */}
                <div className="glass-card animate-scale-in" style={{ textAlign: 'left', background: 'var(--bg-surface)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
                    <MapPin size={24} />
                    <h2 style={{ fontSize: '1.3rem', margin: 0 }}>Ubicación</h2>
                  </div>
                  <div style={{ height: '350px', marginTop: '1rem', borderRadius: '12px', overflow: 'hidden', background: '#eee' }}>
                    <Suspense fallback={<div className="spinner">Cargando mapa...</div>}>
                      {showMap && <Map position={position} zoom={15} />}
                    </Suspense>
                  </div>
                </div>

                {/* Información de Contacto */}
                <div className="glass-card animate-scale-in" style={{ padding: '2rem', textAlign: 'left', background: 'var(--bg-surface)' }}>
                  <h2 style={{ fontSize: '1.25rem', color: 'var(--text-main)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Phone size={24} color="var(--primary)" />
                    Información de Contacto
                  </h2>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: 'var(--bg-body)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                    <div style={{ color: 'var(--primary)' }}><MapPin size={24} /></div>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dirección</div>
                      <div style={{ fontWeight: 500, color: 'var(--text-main)' }}>{data.direccion}</div>
                    </div>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: 'var(--bg-body)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                    <div style={{ color: 'var(--primary)' }}><Phone size={24} /></div>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Teléfonos</div>
                      <div style={{ fontWeight: 500, color: 'var(--text-main)' }}>{data.telefonos}</div>
                    </div>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: 'var(--bg-body)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                    <div style={{ color: 'var(--primary)' }}><Mail size={24} /></div>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Correo Electrónico</div>
                      <div style={{ fontWeight: 500, color: 'var(--text-main)' }}>{data.email}</div>
                    </div>
                  </li>

                  <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: 'var(--text-main)' }}>Horarios</h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {data.horarios?.map((item, idx) => (
                        <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                          <span style={{ color: 'var(--primary)', marginTop: '2px' }}><Clock size={20} /></span>
                          <div>
                            <strong style={{ display: 'block', fontSize: '0.95rem', color: 'var(--text-main)' }}>{item.label}</strong>
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{item.time}</span>
                          </div>
                        </li>
                    ))}
                  </ul>
                </ul>
              </div>
              {/* Fin Columna Derecha */}
              </div>

            </div>
          </div>
        </section>
      </div>
  );
}